import { useState, useEffect, useCallback } from 'react';
import {  useDispatch, useSelector } from 'react-redux';
//components
import AssetOverview from '../components/AssetOverview';
import DropdownWithCheckboxes from '../../../components/dropdowns/muiSelect/muiMultiSelect';
import EmptyState from '../components/EmptyState';
//constants
import { ReportsCategory } from '../../../constants';
import { setSelectedCategories, setSelectedStates, updateCollapsedStates, updateFilteredData, updateZoomOutView } from '../../../store/slices/reportsSlice';
import CentralizedAssetOverview from '../components/AssetOverview/CentralizedAssetOverview';
//assets
import ViewCompactIcon from '@mui/icons-material/ViewCompact';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';


const useReportSharedLogic = () => {
    const dispatch = useDispatch();

    const { 
        filteredData, inventoryCategories, roomsCategories, hasBaseline, 
        classifiedCategories, previewMode, zoomOutView, auditoryNotes,
        roomsData, checkboxStates, descriptions, selectedCategories 
    } = useSelector((state) => state.reports);
    
    const [tabIndex, setTabIndex] = useState('1');
    const [notifState, setNotifState] = useState(null);
    const [unfoldAll, setUnfoldAll] = useState(false);

    const handleNotifState = (notification) => {
        setNotifState(notification);
    };

    const handleChangeTabIndex = (event, newValue) => {
        setTabIndex(newValue);
        dispatch(updateFilteredData(newValue === "1" ? ReportsCategory.INVENTORY : newValue === "2" ? ReportsCategory.DEFECTS : ReportsCategory.ORGANIZATION));
    };

    useEffect(() => {
        roomsData.reduce((acc1, r) => {
            if (!acc1.includes(r.title)) acc1.push(r.title);
            return acc1;
        }, [])
        .forEach((r) => {
            const roomData = filteredData.filter((item) => item.room === r);
            const roomIdentifications = roomData.map((item) => item.object_id);
            const roomClassifications = [...new Set(
                roomIdentifications.map((id) => classifiedCategories?.[id]).filter((c) => c)
            )];
            roomClassifications.sort();

            const targets = [r,...roomClassifications.map((c) => `${r}-${c}`)];
            targets.forEach((t) => {
                dispatch(updateCollapsedStates({ section: t, collapse: unfoldAll }))
            });
        });
    }, [unfoldAll]);

    const renderCentralizedView = useCallback(() => {
        if (filteredData && filteredData.length === 0) {
            return (
                <div className='flex flex-col'>
                    <EmptyState />
                </div>
            );
        } else if ((roomsData || []).length === 0) {
            return (
                <div className='flex flex-col'>
                    <EmptyState />
                </div>
            );
        }

        const roomsList = roomsData.reduce((acc1, r) => {
            if (!acc1.includes(r.title)) acc1.push(r.title);
            return acc1;
        }, []);

        return (
            <div className='flex flex-col pos-relative'>
                <div className={'flex flex-wrap w-full my-4' + (previewMode ? ' d-none' : '')} style={{gap: '12px'}}>
                    <DropdownWithCheckboxes
                        options={roomsList.reduce((acc, category) => ({...acc, [category]: {primary: category}}), {})}
                        onChange={(selected) => { 
                            dispatch(setSelectedCategories(selected));
                            dispatch(updateFilteredData(ReportsCategory.CENTRALIZED));
                        }}
                        label="All Rooms"
                        scrollable={true}
                        searchable={true}
                    />
                    <DropdownWithCheckboxes
                        options={{'OK': {primary: 'OK'}, 'Attention Required': {primary: 'Attention Required'}}}
                        onChange={(selected) => { 
                            dispatch(setSelectedStates(selected));
                            dispatch(updateFilteredData(ReportsCategory.CENTRALIZED));
                        }}
                        label="All States"
                        scrollable={true}
                    />
                    <div className="mt-1">
                        <button
                            className="cursor-pointer rounded-full bg-white shadow-sm px-2 py-2 border border-gray-200" 
                            title="Change Items Layout"
                            onClick={() => dispatch(updateZoomOutView(!zoomOutView))}
                        >
                            {zoomOutView ? 
                                <ViewCompactIcon sx={{ color: '#3C3EB8', fontSize: '25px' }}/> : 
                                <ViewColumnIcon sx={{ color: '#3C3EB8', fontSize: '25px' }}/>
                            }
                        </button>
                    </div>
                    <div className="mt-1">
                        <button
                            className="cursor-pointer rounded-full bg-white shadow-sm px-2 py-2 border border-gray-200" 
                            title={unfoldAll ? "Collapse All" : "Expand All"}
                            onClick={() => setUnfoldAll(!unfoldAll)}
                        >
                            {unfoldAll ? 
                                <UnfoldLessIcon sx={{ color: '#3C3EB8', fontSize: '25px' }}/> : 
                                <UnfoldMoreIcon sx={{ color: '#3C3EB8', fontSize: '25px' }}/>
                            }
                        </button>
                    </div>
                </div>
                {roomsList.map((room) => {
                    const roomData = filteredData.filter((item) => item.room === room);
                    const roomAuditoryNotes = auditoryNotes.filter((note) => note.room === room && (!previewMode || checkboxStates?.[ReportsCategory.CENTRALIZED]?.[note.object_id]));
                    const roomIdentifications = roomData.map((item) => item.object_id);
                    const roomClassifications = [...new Set(
                        roomIdentifications.map((id) => classifiedCategories?.[id]).filter((c) => c)
                    )];
                    roomClassifications.sort();
                    
                    return (
                        <CentralizedAssetOverview
                            assetItems={roomData}
                            assetItemsIndexes={
                                filteredData
                                .map((item, index) => [item, index])
                                .filter(([item, index]) => item.room === room)
                                .map(([_, index]) => index)
                            }
                            auditoryItems={roomAuditoryNotes}
                            groups={roomClassifications}
                            header={room}
                            setNotifState={setNotifState}
                        />
                    )
                })
                }
            </div>
        )
        
    });

    const renderTabContent = useCallback((dataKey) => {
        if (filteredData && filteredData.length === 0) {
            return (
                <div className='flex flex-col'>
                    <EmptyState />
                </div>
            );
        }
        const usedCategories = (
                (roomsData || []).length === 0 || 
                (hasBaseline && [ReportsCategory.INVENTORY, ReportsCategory.ORGANIZATION].includes(dataKey))
            ) ? inventoryCategories : roomsCategories[dataKey];
        const usingCategories = dataKey === ReportsCategory.INVENTORY || (roomsData || []).length > 0;


        return (
            <div className='flex flex-col'>
                {usingCategories && (
                    <div className='flex w-52 my-4'>
                        <DropdownWithCheckboxes
                            options={Object.keys(usedCategories).filter((k) => usedCategories[k].length > 0).reduce((acc, category) => ({...acc, [category]: {primary: category}}), {})}
                            onChange={(selected) => { 
                                dispatch(setSelectedCategories(selected));
                                dispatch(updateFilteredData(ReportsCategory.INVENTORY));
                            }}
                            label="All Categories"
                            scrollable={true}
                            searchable={true}
                        />
                    </div>
                )}
                {usingCategories ? 
                    ((filteredData && usedCategories) && (
                        Object.entries(usedCategories).filter(
                            ([category, items]) => selectedCategories === null || selectedCategories.length === 0 || selectedCategories.includes(category)
                        ).map(([category, items]) => (
                            <AssetOverview
                                assetItems={filteredData.filter((item) => items.includes(item.object_id))}
                                assetItemsIndexes={
                                    filteredData
                                    .map((item, index) => [item.object_id, index])
                                    .filter(([object_id, idx]) => items.includes(object_id))
                                    .map(([object_id, idx]) => idx)
                                }
                                header={category}
                                checkboxStates={checkboxStates}
                                descriptions={descriptions}
                                setNotifState={setNotifState}
                            />
                        ))
                    ))
                    :
                    (filteredData &&
                        <AssetOverview
                            assetItems={filteredData}
                            assetItemsIndexes={filteredData.map((_, index) => index)}
                            header={"All Items"}
                            checkboxStates={checkboxStates}
                            descriptions={descriptions}
                            setNotifState={setNotifState}
                        />
                    )
                }
            </div>
        );
    }, [dispatch, filteredData, inventoryCategories, selectedCategories, checkboxStates, descriptions]);

    return {
        tabIndex,
        notifState,
        handleNotifState,
        handleChangeTabIndex,
        renderTabContent,
        renderCentralizedView
    };
};

export default useReportSharedLogic;